import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Menu, MenuItem, SubMenu } from "@szhsin/react-menu";
import "@szhsin/react-menu/dist/index.css";
import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { OrgContext } from "./common/OrgContext";
import { UserContext } from "./common/UserContext";
import { Organization, User, makeRequest } from "./common/api";
import { isUserCreator } from "./common/auth";

setTimeout(() => {
  const params = new URLSearchParams(window.location.search);
  const orgIdParam = params.get("organizationId");
  if (orgIdParam) {
    window.localStorage.setItem("organizationId", orgIdParam);
  }
}, 20);

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid #eeeeee;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 32px;
  padding: 0 24px;
  max-width: 1200px;
  margin: 0 auto;
  height: 64px;
  font-family: "Montserrat", sans-serif;

  @media (max-width: 480px) {
    padding: 0 16px;
    gap: 16px;
  }
`;

const NavLink = styled(Link)`
  color: #555;
  font-weight: 500;
  font-size: 15px;
  text-decoration: none;
  cursor: pointer;
  padding: 4px 0;
  position: relative;
  white-space: nowrap;

  &::after {
    content: "";
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background-color: #9051a8;
    transition: width 0.2s ease;
  }

  &:hover,
  &:focus {
    color: #9051a8;
    &::after {
      width: 100%;
    }
  }
`;

const NavImg = styled.img`
  height: 40px;
  display: block;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 28px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const UserMenu = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
`;

const UserAvatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background-color: #9051a8;
  color: white;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
  user-select: none;
  flex-shrink: 0;
`;

const LogoutLink = styled.span`
  color: #9051a8;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const HamburgerButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #555;
  font-size: 22px;
  line-height: 1;

  &:hover,
  &:focus {
    background-color: transparent;
    color: #9051a8;
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MobileMenu = styled.div<{ open: boolean }>`
  display: none;
  font-family: "Montserrat", sans-serif;

  @media (max-width: 768px) {
    display: ${(p) => (p.open ? "flex" : "none")};
    flex-direction: column;
    background: white;
    border-top: 1px solid #eee;
    padding: 8px 0;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 99;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
  }
`;

const MobileNavLink = styled(Link)`
  display: block;
  padding: 12px 24px;
  color: #444;
  font-size: 15px;
  font-weight: 500;
  text-decoration: none;
  font-family: "Montserrat", sans-serif;

  &:hover {
    background-color: #f5f3ff;
    color: #9051a8;
  }
`;

const MobileNavAction = styled.span`
  display: block;
  padding: 12px 24px;
  color: #9051a8;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #f5f3ff;
  }
`;

export const NavBar = () => {
  const userContext = React.useContext(UserContext);
  const user = userContext?.user;
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const logout = async () => {
    await makeRequest("/api/logout", "POST");
    userContext?.logout();
    window.location.href = "/";
  };

  const initials = user?.email ? user.email.slice(0, 2).toUpperCase() : "?";

  return (
    <>
      <Header>
        <HeaderContent>
          {/* Logo */}
          <a
            href="https://upliftcodecamp.com/"
            rel="noopener noreferrer"
            target="_blank"
          >
            <NavImg src="/logo.png" />
          </a>

          {/* Desktop nav links */}
          <NavLinks>
            <NavLink to="/enroll">Enroll</NavLink>
            {isUserCreator(user) && (
              <NavLink to="/admin/dashboard">Dashboard</NavLink>
            )}
            {isUserCreator(user) && (
              <NavLink to="/admin/reports">Reports</NavLink>
            )}
            {isUserCreator(user) && (
              <NavLink to="/admin/config">Config</NavLink>
            )}
          </NavLinks>

          {/* Right side: CTA / user controls */}
          <UserMenu>
            {user ? (
              <>
                <UserAvatar title={user.email}>{initials}</UserAvatar>
                <LogoutLink onClick={logout}>Logout</LogoutLink>
              </>
            ) : (
              <NavLink to="/login">Log in</NavLink>
            )}
            {/* Hamburger — mobile only */}
            <HamburgerButton
              onClick={() => setMobileOpen((o) => !o)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? "✕" : "☰"}
            </HamburgerButton>
          </UserMenu>
        </HeaderContent>

        {/* Mobile dropdown menu */}
        <MobileMenu open={mobileOpen}>
          <MobileNavLink to="/enroll" onClick={() => setMobileOpen(false)}>
            Enroll
          </MobileNavLink>
          {isUserCreator(user) && (
            <MobileNavLink
              to="/admin/dashboard"
              onClick={() => setMobileOpen(false)}
            >
              Dashboard
            </MobileNavLink>
          )}
          {isUserCreator(user) && (
            <MobileNavLink
              to="/admin/reports"
              onClick={() => setMobileOpen(false)}
            >
              Reports
            </MobileNavLink>
          )}
          {isUserCreator(user) && (
            <MobileNavLink
              to="/admin/config"
              onClick={() => setMobileOpen(false)}
            >
              Config
            </MobileNavLink>
          )}
          {user ? (
            <MobileNavAction
              onClick={() => {
                setMobileOpen(false);
                logout();
              }}
            >
              Logout
            </MobileNavAction>
          ) : (
            <MobileNavLink to="/login" onClick={() => setMobileOpen(false)}>
              Log in
            </MobileNavLink>
          )}
        </MobileMenu>
      </Header>
    </>
  );
};

interface UserDropdownProps {
  user: User | null;
}
export const UserDropdown = (props: UserDropdownProps) => {
  const org = React.useContext(OrgContext);
  const [organizations, setOrganizations] = React.useState<
    Organization[] | null
  >(null);

  const fetchOrganizations = async () => {
    let orgs = organizations || [];
    for (let orgRole of props.user.orgRoles) {
      if (org && org !== "NONE" && orgRole.orgId === org._id) {
        orgs = [...orgs, org];
        setOrganizations(orgs);
      } else {
        const orgItem = await makeRequest(
          `/api/organizations/${orgRole.orgId}`,
          "GET",
        );
        orgs = [...orgs, orgItem];
        setOrganizations(orgs);
      }
    }
  };
  React.useEffect(() => {
    if (props.user && !organizations) {
      setOrganizations([]);
      fetchOrganizations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizations, props.user]);

  return (
    <Menu
      menuButton={
        <span style={{ cursor: "pointer" }}>
          {props.user.email} <FontAwesomeIcon icon="chevron-down" />
        </span>
      }
      portal={true}
      align="end"
      direction="bottom"
      position="initial"
    >
      <SubMenu label="Organizations">
        {props.user.orgRoles.map((role) => {
          const knownOrgs = [...(organizations || [])];
          if (org && org !== "NONE") {
            knownOrgs.push(org);
          }

          return (
            <MenuItem key={role.orgId}>
              <FontAwesomeIcon icon="check" />
              {knownOrgs.find((o) => o._id === role.orgId)?.name} - {role.role}
            </MenuItem>
          );
        })}
      </SubMenu>
    </Menu>
  );
};
